import { Component } from "../base/Component";
import { ComponentFactory } from "../../service/ComponentFactory";
import { PageContext } from "../../service/PageContext";
import { WyanokeUtil } from "../../util/WyanokeUtil";

const COLUMNS_NAV_API = "/h5news/columns/collectionsfornav";
const BLOGS_AND_COLUMNS_ROOT = "/news/blogs-and-columns";

export class NewsHeaderLinks extends Component {

    constructor(element: HTMLElement, context: PageContext) {
        super(element, context);
    }

    public async init(): Promise<void> {
        this.removeColumnsCaretUntilPopulated();
        const profile = await WyanokeUtil.loadUserProfile(true, false);
        const specialtyForApi = await this.getSpecialtyForColumnsApi(profile);
        if (!specialtyForApi) {
            this.setColumnsAsLinkOnly(BLOGS_AND_COLUMNS_ROOT);
        } else {
            await this.fetchAndApplyColumnsNav(specialtyForApi);
        }

        if (!profile || profile.isLoggedIn === "False") {
            return;
        }

        const specs = this.getSpecialtySlugs();
        var userSpec: string = (profile.contentspecialty ?? "")
            .split("/").join("-").split(" & ").join("-").split("'").join("").split(" ").join("-").toLowerCase();

        //please ignore the following lines
        if (userSpec === "" && profile?.profession === "Optometrist") {
            userSpec = "optometry";
        }
        //ok you can look now

        //set all links to their specialty version
        document.querySelectorAll<HTMLAnchorElement>("[data-specialty-href]").forEach(function (element) {
            if (specs.indexOf(userSpec) >= 0) {
                element.href = element.dataset.specialtyHref?.replace("/$slug$", "/" + userSpec) ?? "";
            } else {
                let newTarget = element.dataset.specialtyHref?.replace("/$slug$", "");
                if (newTarget === "") {
                    newTarget = "/";
                }
                element.href = newTarget ?? "";
            }
        });

        //set the news link to the specialty version
        if (specs.indexOf(userSpec) >= 0) {
            let atag = document.querySelector<HTMLAnchorElement>(".global-nav__navbar > .navbar-nav > .active > .nav-link");
            if (atag) {
                atag.href += "/" + userSpec;
            }
        }
    }

    private getSpecialtySlugs(): string[] {
        const dropdown = document.querySelector(".specialty-dropdown");
        if (!dropdown) return [];
        return [...dropdown.querySelectorAll<HTMLElement>(".list-group-item")]
            .map((e) => e.dataset.specname ? e.dataset.specname.split(" ").join("-") : undefined)
            .filter((s): s is string => typeof s === "string");
    }

    private async getSpecialtyForColumnsApi(profile: { isLoggedIn?: string; contentspecialty?: string; profession?: string } | null): Promise<string | null> {
        if (!profile || profile.isLoggedIn === "False") return null;
        let userSpec = profile.contentspecialty?.split("/").join("-").split(" & ").join("-").split("'").join("").split(" ").join("-").toLowerCase() ?? "";
        if (userSpec === "" && profile?.profession === "Optometrist") userSpec = "optometry";
        return userSpec || null;
    }

    private isColumnsContextPath(): boolean {
        const path = (window.location.pathname || "").replace(/\/$/, "").toLowerCase();
        return path.indexOf("/columns") >= 0 || path.indexOf("/blogs-and-columns") >= 0;
    }

    /** Remove the Columns dropdown caret (dropdown-toggle class) until we know the list is populated (avoids blip when showing link-only). */
    private removeColumnsCaretUntilPopulated(): void {
        document.querySelectorAll<HTMLElement>("[data-columns-nav]").forEach((nav) => {
            const toggle = nav.querySelector<HTMLAnchorElement>(".nav-link.dropdown-toggle");
            if (toggle) toggle.classList.remove("dropdown-toggle");
        });
    }

    /** Restore the Columns dropdown caret and dropdown behavior (call when list is populated). */
    private restoreColumnsDropdownCaret(): void {
        document.querySelectorAll<HTMLElement>("[data-columns-nav].columns-dropdown").forEach((nav) => {
            const toggle = nav.querySelector<HTMLAnchorElement>(".nav-link");
            if (toggle && !toggle.classList.contains("dropdown-toggle")) {
                toggle.classList.add("dropdown-toggle");
                toggle.setAttribute("data-toggle", "dropdown");
                toggle.setAttribute("aria-expanded", "false");
            }
        });
    }

    /**
     * Show Columns as a single link (no dropdown). Used when the user has no
     * specialty (e.g. logged out) or when their specialty has no collections.
     * The actual target URL comes from the existing header link (or its
     * data-columns-header child), so behavior matches the original nav.
     */
    private setColumnsAsLinkOnly(fallbackHref: string): void {
        const inColumnsContext = this.isColumnsContextPath();
        document.querySelectorAll<HTMLElement>("[data-columns-nav]").forEach((nav) => {
            // Determine the best href to use:
            const headerLink = nav.querySelector<HTMLAnchorElement>("[data-columns-header]");
            const desktopTopLink = nav.classList.contains("columns-dropdown")
                ? nav.querySelector<HTMLAnchorElement>(".nav-link")
                : null;
            const baseHref = headerLink?.href || desktopTopLink?.href || fallbackHref;

            // Desktop: make the top nav link a simple link with no dropdown behavior.
            if (desktopTopLink) {
                desktopTopLink.href = baseHref;
                desktopTopLink.classList.remove("dropdown-toggle");
                desktopTopLink.removeAttribute("data-toggle");
                desktopTopLink.removeAttribute("aria-expanded");
                desktopTopLink.setAttribute("role", "link");
                desktopTopLink.classList.toggle("active", inColumnsContext);
            }

            // Hide the desktop dropdown menu entirely.
            const dropdownMenu = nav.querySelector<HTMLElement>(".dropdown-menu");
            if (dropdownMenu) {
                dropdownMenu.style.display = "none";
            }

            // Mobile: use the existing card-header link if present, and hide the accordion.
            const mobileLink = nav.querySelector<HTMLAnchorElement>("[data-columns-nav-mobile] .card-header .btn-link");
            if (mobileLink) {
                if (headerLink?.href) {
                    mobileLink.href = headerLink.href;
                } else if (mobileLink.href === "#" && baseHref) {
                    mobileLink.href = baseHref;
                }
                mobileLink.classList.toggle("active", inColumnsContext);
            }
            const accordionBtn = nav.querySelector<HTMLElement>("[data-columns-nav-mobile] .accordion-button");
            if (accordionBtn) {
                accordionBtn.style.display = "none";
            }
            const collapseEl = nav.querySelector<HTMLElement>("#collapseColumns");
            if (collapseEl) {
                collapseEl.style.display = "none";
            }
        });
    }

    /**
     * Normalize specialty/collection display names for the Columns nav.
     * Currently only fixes a couple of known cases.
     */
    private normalizeDisplayName(name: string): string {
        if (!name) return name;
        let result = name.trim();
        result = result.replace("Hematology Oncology", "Hematology/Oncology");
        result = result.replace("Allergy Asthma", "Allergy/Asthma");
        return result;
    }

    private async fetchAndApplyColumnsNav(specialty: string | null): Promise<void> {
        const url = specialty ? `${COLUMNS_NAV_API}?specialty=${encodeURIComponent(specialty)}` : COLUMNS_NAV_API;
        try {
            const res = await fetch(url);
            if (!res.ok) return;
            const data = await res.json() as { headerText: string; items: Array<{ title: string; url: string }> };
            const allItems = Array.isArray(data.items) ? data.items : [];
            if (!specialty || allItems.length === 0) {
                const linkHref = specialty ? `/news/${specialty}/blogs-and-columns` : BLOGS_AND_COLUMNS_ROOT;
                this.setColumnsAsLinkOnly(linkHref);
                return;
            }
            const itemsToRender = allItems.slice(0, 10);
            this.restoreColumnsDropdownCaret();
            document.querySelectorAll<HTMLElement>("[data-columns-header]").forEach((el) => {
                const headerText = this.normalizeDisplayName(data.headerText ?? "All Columns");
                el.textContent = headerText;
            });
            const allColumnsHref = specialty ? `/news/${specialty}/blogs-and-columns` : BLOGS_AND_COLUMNS_ROOT;
            const currentPath = (window.location.pathname || "").replace(/\/$/, "").toLowerCase();
            const norm = (u: string) => (u || "").replace(/\/$/, "").toLowerCase();
            const isOnAllColumnsPage = norm(allColumnsHref) === currentPath;
            const anyColumnMatches = allItems.some((item) => item?.url && (norm(item.url) === currentPath || currentPath.startsWith(norm(item.url) + "/")));
            const isAllColumnsItemActive = isOnAllColumnsPage || !anyColumnMatches;
            const inColumnsContext = isOnAllColumnsPage || (currentPath.indexOf("/columns") >= 0);
            document.querySelectorAll<HTMLElement>("[data-columns-nav-list]").forEach((listEl) => {
                const isUl = listEl.tagName === "UL";
                const rawHeaderText = data.headerText ?? "All Columns";
                const headerText = this.normalizeDisplayName(rawHeaderText);
                const fragment = document.createDocumentFragment();
                let anyActive = false;
                if (isUl) {
                    const headerLi = document.createElement("li");
                    headerLi.className = isAllColumnsItemActive ? "nav-item active" : "nav-item";
                    if (isAllColumnsItemActive) anyActive = true;
                    const headerLink = document.createElement("a");
                    headerLink.href = allColumnsHref;
                    headerLink.className = "nav-link";
                    headerLink.setAttribute("data-columns-header", "");
                    headerLink.textContent = headerText;
                    headerLi.appendChild(headerLink);
                    fragment.appendChild(headerLi);
                } else {
                    const headerLink = document.createElement("a");
                    headerLink.href = allColumnsHref;
                    headerLink.className = isAllColumnsItemActive ? "list-group-item active" : "list-group-item";
                    if (isAllColumnsItemActive) anyActive = true;
                    headerLink.setAttribute("data-columns-header", "");
                    headerLink.textContent = headerText;
                    fragment.appendChild(headerLink);
                }
                itemsToRender.forEach((item) => {
                    if (!item.url || !item.title) return;
                    const displayTitle = this.normalizeDisplayName(item.title);
                    const itemPath = norm(item.url);
                    const isOnColumnPage = itemPath === currentPath || currentPath.startsWith(itemPath + "/");
                    if (isOnColumnPage) anyActive = true;
                    if (isUl) {
                        const li = document.createElement("li");
                        li.className = isOnColumnPage ? "nav-item active" : "nav-item";
                        const a = document.createElement("a");
                        a.className = "nav-link";
                        a.href = item.url;
                        // a.setAttribute("data-module-track-action", `News Global Nav_Click_${displayTitle}`);
                        //a.setAttribute("data-module-track-label", `${displayTitle}_News Global Nav`);
                        a.textContent = displayTitle;
                        li.appendChild(a);
                        fragment.appendChild(li);
                    } else {
                        const a = document.createElement("a");
                        a.href = item.url;
                        a.className = isOnColumnPage ? "list-group-item active" : "list-group-item";
                        //a.setAttribute("data-module-track-action", `News Global Nav_Click_${displayTitle}`);
                        //a.setAttribute("data-module-track-label", `${displayTitle}_News Global Nav`);
                        a.textContent = displayTitle;
                        fragment.appendChild(a);
                    }
                });
                listEl.innerHTML = "";
                listEl.appendChild(fragment);
                const isHeaderActive = !anyActive && inColumnsContext;
                const columnsNav = listEl.closest("[data-columns-nav]");
                if (columnsNav) {
                    const dropdownToggle = columnsNav.querySelector(".nav-link.dropdown-toggle");
                    if (dropdownToggle) {
                        dropdownToggle.classList.toggle("active", isHeaderActive);
                    }
                    const mobileHeaderLink = columnsNav.querySelector("[data-columns-nav-mobile] .card-header .btn-link");
                    if (mobileHeaderLink) {
                        mobileHeaderLink.classList.toggle("active", inColumnsContext);
                    }
                }
            });
        } catch {
            // leave server-rendered columns as-is
        }
    }
}

ComponentFactory.registerComponent("NewsHeaderLinks", NewsHeaderLinks);